//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { RightsRequestCommunicationType } from '@apis/rights-management/rightsManagementApiModels';
import { RequiredPermissionsButton } from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { CaptionSecondaryText } from '@components/atoms/text/Text';
import Cuid from '@models/Cuid';
import {
  UIATrackingIds,
  UIA_RIGHTS_REQUEST_PERMISSION,
} from '@uia/constants/uiaConstants';
import { useInitiateRightsMutation } from '@uia/rights/hooks/useInitiateRightsMutation';

export interface ExtendedRightsRequestProps {
  cuid: Cuid;
  communicationType: RightsRequestCommunicationType | undefined;
  previousApprovalPhrase: string | undefined;
  rightsRequestText: string | undefined;
}

export const ExtendedRightsRequest = ({
  communicationType,
  cuid,
  previousApprovalPhrase,
  rightsRequestText,
}: ExtendedRightsRequestProps) => {
  const { mutate: initiateRights } = useInitiateRightsMutation();

  return (
    <FlexStart>
      <RequiredPermissionsButton
        onClick={() =>
          initiateRights({
            cuid,
            request: {
              approvalPhrase: previousApprovalPhrase,
              communicationType,
              requestMessage: rightsRequestText,
            },
          })
        }
        permissionName={UIA_RIGHTS_REQUEST_PERMISSION}
        trackingId={UIATrackingIds.RIGHTS_REQUEST_EXTEND_RIGHTS_REQUEST_BUTTON}
      >
        Extend Rights Request
      </RequiredPermissionsButton>
      <CaptionSecondaryText>
        This will extend the time the owner of this post has to respond to your
        rights request. Requests are considered Rejected if the owner hasn't
        responded within 3 weeks.
      </CaptionSecondaryText>
    </FlexStart>
  );
};

const FlexStart = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    gap: ${theme.spacings[2]};
  `,
);
